import {
  EXECUTION_MODES,
  type CardLabel,
  type ExecutionChoice,
  type ExecutionMode,
  type ExecutionModePatch,
  type ExecutionModeFormValues,
  type HomeownerProInput,
  type HomeownerProSource,
  type ImprovementPresets,
  type ProFormErrors,
  type ProFormValues,
  type ReportItem,
  type ReusablePro,
  type SelectorState,
} from './types';
import type { FlowEventMetadata } from '@belong/common';
import { STRINGS } from './strings';
import {
  email as validateEmail,
  phoneValidation,
  required,
} from 'utils/validation';

const OPTIONAL_ASSESSMENTS = ['Inspirational', 'Recommended'];

const hasResidentResponsibility = (item: ReportItem): boolean => {
  const { maintenancePayments } = item;
  const residentPayment = maintenancePayments.find(
    (p) => p.paidBy === 'Resident',
  );

  return Boolean(residentPayment && (residentPayment.percentage ?? 0) > 0);
};

export function hasExecutionModeSupport(items: ReportItem[]): boolean {
  return items.some(
    (reportItem) => typeof reportItem.maintenance?.executionMode === 'string',
  );
}

export function getItemExecutionMode(reportItem: ReportItem): ExecutionMode {
  const stored = reportItem.maintenance.executionMode;
  if (stored && EXECUTION_MODES.includes(stored)) return stored;

  switch (reportItem.maintenance.proServiceResponsibility) {
    case 'Homeowner':
      return 'HomeownerFixing';
    case 'Resident':
      return 'ResidentFixing';
    default:
      return 'BelongPro';
  }
}

export function deriveResponsibility(
  mode: ExecutionMode,
): 'Homeowner' | 'Resident' | 'Belong' {
  if (mode === 'HomeownerFixing') return 'Homeowner';
  if (mode === 'ResidentFixing') return 'Resident';
  return 'Belong';
}

export function isSelectorGated(
  reportItem: ReportItem,
  presets: ImprovementPresets | undefined,
): boolean {
  const { maintenance } = reportItem;
  const preset = presets?.[maintenance.summary ?? ''];

  return (
    Boolean(preset?.allowHomeownerFixing) &&
    !hasResidentResponsibility(reportItem) &&
    !OPTIONAL_ASSESSMENTS.includes(maintenance.assessment ?? '') &&
    !maintenance.shouldPreventHomeownerFixing
  );
}

function hasContact(input: HomeownerProInput | undefined): boolean {
  return Boolean(input?.email?.trim() || input?.phone?.trim());
}

export function applyExecutionModePatch(
  reportItem: ReportItem,
  patch: ExecutionModePatch,
): ReportItem {
  const { homeownerProInput: _dropped, ...maintenance } =
    reportItem.maintenance;

  return {
    ...reportItem,
    maintenance: {
      ...maintenance,
      executionMode: patch.executionMode,
      proServiceResponsibility: deriveResponsibility(patch.executionMode),
      ...(patch.homeownerProInput && {
        homeownerProInput: patch.homeownerProInput,
      }),
    },
  };
}

export function buildPutItem<T extends ReportItem>(reportItem: T): T {
  const {
    homeownerPro: _readOnly,
    homeownerProInput,
    ...maintenance
  } = reportItem.maintenance;

  const keepInput =
    maintenance.executionMode === 'HomeownerPro' &&
    Boolean(homeownerProInput?.name.trim()) &&
    hasContact(homeownerProInput);

  return {
    ...reportItem,
    maintenance: {
      ...maintenance,
      ...(keepInput && { homeownerProInput }),
    },
  };
}

export function clearOwnProChoices<T extends ReportItem>(
  items: T[],
  modes: ExecutionMode[],
): T[] {
  return items.map((reportItem) =>
    modes.includes(getItemExecutionMode(reportItem))
      ? (applyExecutionModePatch(reportItem, {
          executionMode: 'BelongPro',
        }) as T)
      : reportItem,
  );
}

export function getReusablePros(items: ReportItem[]): ReusablePro[] {
  const byPartner = new Map<number, ReusablePro>();

  for (const reportItem of items) {
    const pro = reportItem.maintenance.homeownerPro;
    if (!pro?.partnerId || !pro.name?.trim()) continue;

    const entry = byPartner.get(pro.partnerId) ?? {
      partnerId: pro.partnerId,
      name: pro.name,
      phone: pro.phone ?? undefined,
      email: pro.email ?? undefined,
      trades: [],
    };

    const { trade } = reportItem.maintenance;
    if (trade && !entry.trades.includes(trade)) entry.trades.push(trade);

    byPartner.set(pro.partnerId, entry);
  }

  return [...byPartner.values()].sort(byDisplayOrder);
}

function byDisplayOrder(a: ReusablePro, b: ReusablePro): number {
  return a.name.localeCompare(b.name) || a.partnerId - b.partnerId;
}

export function mergeReusablePros(
  remembered: ReusablePro[],
  current: ReusablePro[],
): ReusablePro[] {
  const byPartner = new Map(remembered.map((pro) => [pro.partnerId, pro]));

  for (const pro of current) {
    const previous = byPartner.get(pro.partnerId);

    byPartner.set(pro.partnerId, {
      ...pro,
      trades: pro.trades.length ? pro.trades : (previous?.trades ?? []),
    });
  }

  return [...byPartner.values()].sort(byDisplayOrder);
}

const MAX_VISIBLE_TRADES = 3;

function formatTradeName(trade: string): string {
  return trade.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/ And /g, ' and ');
}

export function formatProTrades(trades: string[]): string {
  const named = trades.filter((trade) => trade.trim());
  const visible = named
    .slice(0, MAX_VISIBLE_TRADES)
    .map(formatTradeName)
    .join(', ');
  const hidden = named.length - MAX_VISIBLE_TRADES;

  if (hidden <= 0) return visible;

  return `${visible} ${STRINGS.picker.moreTrades.replace(
    '{count}',
    String(hidden),
  )}`;
}

export function ownProCopyTokens(count: number): {
  count: number;
  item: string;
  itemTitle: string;
  pro: string;
} {
  const many = count !== 1;

  return {
    count,
    item: many ? 'items' : 'item',
    itemTitle: many ? 'Items' : 'Item',
    pro: many ? 'Pros' : 'Pro',
  };
}

function ownProItems(items: ReportItem[]): ReportItem[] {
  return items.filter(
    (reportItem) => getItemExecutionMode(reportItem) === 'HomeownerPro',
  );
}

export function countOwnProItems(items: ReportItem[]): number {
  return ownProItems(items).length;
}

export function areOwnProItemsQuotePending(items: ReportItem[]): boolean {
  const own = ownProItems(items);
  return own.length > 0 && own.every((reportItem) => reportItem.isTBD === true);
}

export function getCardLabel(
  reportItem: ReportItem,
  isGated: boolean,
): CardLabel {
  const homeownerPayment = reportItem.maintenancePayments.find(
    (payment) => payment.paidBy === 'Homeowner',
  );
  if (homeownerPayment?.flagged) return 'discuss';

  const mode = getItemExecutionMode(reportItem);
  if (mode === 'HomeownerPro') return 'ownPro';
  if (mode === 'HomeownerFixing') return 'homeownerFixing';
  if (mode === 'BelongPro' && isGated) return 'belongPro';
  return null;
}

export function resolveExecutionMode(
  values: ExecutionModeFormValues,
): ExecutionMode {
  if (values.manageProMyself) return 'HomeownerFixing';
  return values.executionChoice ?? 'BelongPro';
}

const EXECUTION_CHOICE_BY_MODE: Record<
  ExecutionMode,
  ExecutionChoice | undefined
> = {
  BelongPro: 'BelongPro',
  HomeownerPro: 'HomeownerPro',
  HomeownerFixing: undefined,
  ResidentFixing: 'BelongPro',
};

export function getInitialFormValues(
  reportItem: ReportItem,
  reusablePros: ReusablePro[],
): Pick<
  ExecutionModeFormValues,
  'executionChoice' | 'manageProMyself' | 'homeownerProChoice'
> {
  const mode = getItemExecutionMode(reportItem);
  const partnerId =
    reportItem.maintenance.homeownerPro?.partnerId ??
    reusablePros[0]?.partnerId;

  return {
    executionChoice: EXECUTION_CHOICE_BY_MODE[mode],
    manageProMyself: mode === 'HomeownerFixing',
    homeownerProChoice: partnerId === undefined ? undefined : String(partnerId),
  };
}

const HIDDEN_SELECTOR: SelectorState = {
  showSelector: false,
  buttonsDisabled: false,
  manageMyselfDisabled: false,
  showWarning: false,
  showProPicker: false,
  showProForm: false,
  ctaLabel: STRINGS.cta.saveAndClose,
};

export function getSelectorState({
  values,
  isGated,
  reusablePros,
  isExclusivitySelected,
}: {
  values: ExecutionModeFormValues;
  isGated: boolean;
  reusablePros: ReusablePro[];
  isExclusivitySelected: boolean;
}): SelectorState {
  if (!isGated || values.flagged) return HIDDEN_SELECTOR;

  const mode = resolveExecutionMode(values);
  const managingMyself = mode === 'HomeownerFixing';
  const ownPro = mode === 'HomeownerPro';
  const showProPicker =
    ownPro && reusablePros.length > 0 && values.homeownerProChoice !== 'new';

  return {
    showSelector: true,
    buttonsDisabled: isExclusivitySelected,
    manageMyselfDisabled: isExclusivitySelected,
    showWarning: managingMyself,
    showProPicker,
    showProForm: ownPro && !showProPicker,
    ctaLabel: STRINGS.cta.next,
  };
}

export function getHomeownerProSource(
  values: ExecutionModeFormValues,
  currentPartnerId?: number,
): HomeownerProSource | undefined {
  if (resolveExecutionMode(values) !== 'HomeownerPro') return undefined;

  const choice = values.homeownerProChoice;
  if (!choice || choice === 'new') return 'new';
  if (currentPartnerId !== undefined && choice === String(currentPartnerId))
    return undefined;
  return 'reused';
}

export function buildExecutionModeEvent({
  item: reportItem,
  previousMode,
  patch,
  homeownerProSource,
  flowMetadata,
}: {
  item: ReportItem;
  previousMode: ExecutionMode;
  patch: ExecutionModePatch;
  homeownerProSource?: HomeownerProSource;
  flowMetadata: FlowEventMetadata;
}): FlowEventMetadata {
  return {
    ...flowMetadata,
    executionMode: patch.executionMode,
    previousExecutionMode: previousMode,
    ...(homeownerProSource && { homeownerProSource }),
    maintenanceUniqueId: reportItem.maintenance.uniqueId,
    trade: reportItem.maintenance.trade,
  };
}

export function validateProForm(pro: ProFormValues | undefined): ProFormErrors {
  const values = pro ?? {};
  const errors: ProFormErrors = {};

  if (required(values.firstName))
    errors.firstName = STRINGS.validation.required;
  if (required(values.lastName)) errors.lastName = STRINGS.validation.required;

  const hasPhone = Boolean(values.phone?.trim());
  const hasEmail = Boolean(values.email?.trim());

  if (hasPhone && phoneValidation(values.phone))
    errors.phone = STRINGS.validation.phone;
  if (hasEmail && validateEmail(values.email))
    errors.email = STRINGS.validation.email;

  if (!hasPhone && !hasEmail) {
    errors.phone = STRINGS.validation.contact;
    errors.email = STRINGS.validation.contact;
  }

  return errors;
}

export function validateExecutionModeForm(
  values: ExecutionModeFormValues,
  { isGated, reusablePros }: { isGated: boolean; reusablePros: ReusablePro[] },
): Record<string, unknown> {
  const state = getSelectorState({
    values,
    isGated,
    reusablePros,
    isExclusivitySelected: false,
  });

  if (state.showProForm) {
    const errors = validateProForm(values.homeownerPro);
    return Object.keys(errors).length ? { homeownerPro: errors } : {};
  }

  if (state.showProPicker && !values.homeownerProChoice) {
    return { homeownerProChoice: STRINGS.validation.proRequired };
  }

  return {};
}

export function buildExecutionModePatch(
  values: ExecutionModeFormValues,
  reusablePros: ReusablePro[],
  currentPartnerId?: number,
): ExecutionModePatch {
  const executionMode = resolveExecutionMode(values);
  if (executionMode !== 'HomeownerPro') return { executionMode };

  const choice = values.homeownerProChoice;
  if (choice && choice !== 'new') {
    if (currentPartnerId !== undefined && choice === String(currentPartnerId)) {
      return { executionMode };
    }
    const pro = reusablePros.find(
      (candidate) => String(candidate.partnerId) === choice,
    );
    if (pro) {
      return {
        executionMode,
        homeownerProInput: trimInput({
          name: pro.name,
          phone: pro.phone,
          email: pro.email,
        }),
      };
    }
  }

  if (!values.homeownerPro) return { executionMode };

  const form = values.homeownerPro;
  return {
    executionMode,
    homeownerProInput: trimInput({
      name: [form.firstName, form.lastName]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' '),
      company: form.company,
      phone: form.phone,
      email: form.email,
    }),
  };
}

function trimInput(input: HomeownerProInput): HomeownerProInput {
  const trimmed: HomeownerProInput = { name: input.name.trim() };
  if (input.company?.trim()) trimmed.company = input.company.trim();
  if (input.phone?.trim()) trimmed.phone = input.phone.trim();
  if (input.email?.trim()) trimmed.email = input.email.trim();
  return trimmed;
}
